/**
 * jrs-v2-cart-edit-flow.js — Cart-edit save orchestrator
 *
 * Phase 4 commit 5 (2026-05-21). Replaces the 546 LOC dead-code module
 * that predated Docs 6-9 and was never wired into the V3 stack.
 *
 * ============================================================================
 * Responsibilities
 * ============================================================================
 *
 *  1. Subscribe to `cart:editRequested` events from the cart-edit trigger
 *     (jrs-v2-cart-edit-trigger.js) and route them to
 *     JRS.CustomizerPopup.openFromCartLine(...).
 *
 *  2. Orchestrate the add-before-remove save flow (2026-09-14, ClickUp
 *     86eywgb42 — replaces Doc 9 §5.1's remove-then-add order):
 *       a. Snapshot the original cart lines
 *       b. Build the new payload via existing CartSubmit adapter
 *          (state.draft → ProductConfiguration → cart payload) + preflight
 *       c. ADD the new lines via JRS.CartSubmit.submit
 *       d. Verify the add landed by instance identity (no heal)
 *       e. REMOVE the originals (snapshot keys + original-instance sweep)
 *       f. Verify removal by identity, then JRS.CartSubmit.verifyAfterSubmit
 *       g. On success: fire telemetry, close popup, emit cart:updated
 *       h. On any failure: the originals are only ever removed after the
 *          replacements are proven present; surface error, leave popup open

 *  3. Cancel handling (per Doc 9 §6 — separated from rollback per the
 *     Phase 4 plan §2-Q4 reconciliation): the popup operates on a working
 *     copy in state.draft. Cancel = close popup; cart was never touched.
 *
 * ============================================================================
 * Public surface (JRS.CartEditFlow)
 * ============================================================================
 *
 *   init()            — subscribe to cart:editRequested, auto-called at IIFE eval
 *   destroy()         — unsubscribe (for tests / hot-reload)
 *   saveChanges()     — orchestrate the atomic save. Returns Promise<{ok, ...}>
 *   cancel()          — close popup without saving. Working copy discarded.
 *
 * ============================================================================
 * Failure semantics (Doc 9 §6)
 * ============================================================================
 *
 *   ADD fails (any status, nothing landed) — cart untouched.
 *     reason add_failed_no_mutation, phase 'add'.
 *
 *   ADD partly lands (partialSuccess / addedItems, or a 2xx whose lines are
 *   not in the cart) — landed replacements removed by the replacement
 *   instance id, originals untouched. reason partial_add_reverted; if that
 *   removal fails, partial_add_revert_failed (critical).
 *
 *   ADD or REMOVE hits a 429 challenge — no further cart request that turn.
 *   Edit persisted to sessionStorage (with the cart token) and resumed on the
 *   next load. reason add_challenge_deferred / remove_challenge_deferred,
 *   reloadRequired:true.
 *
 *   ADD ok, REMOVE fails (after one retry on 409) — the item is in the cart
 *   twice. reason remove_after_add_failed (critical), shopper told to remove
 *   the duplicate.
 *
 *   VERIFY — never rolls back. Replacements missing after removal: reason
 *   verify_failed (critical). Heal-pass mismatch: verify_failed telemetry,
 *   edit stands, popup closes.
 *
 *   Per the brief and Phase 4 plan §3 commit 8 / Doc 9 §9.3: the
 *   pdp_customizer_cart_edit_failed and pdp_customizer_cart_edit_
 *   rollback_failed events are NEW additions to Doc 3 v0.3 (see polish
 *   list note in commit 8). The spec'd pdp_customizer_validation_error
 *   from Doc 9 §9.3 is NOT fired by this module — the new events are
 *   more specific and dedicated.
 *
 * ============================================================================
 * Synthetic PDP context
 * ============================================================================
 *
 * The cart-edit popup runs on the cart page, not on a PDP. window.JRS_PDP_
 * CONTEXT doesn't exist. JRS.CartSubmit.buildProductConfiguration needs a
 * context object with productId/productHandle/productTitle/productImage/
 * productColour/sizes/productVariantId. We synthesise these from the
 * clicked cart line (product_title, product_id, featured_image.url,
 * variant_id, vendor, etc.) plus state.draft.sizes (which the parser
 * populated from the union of related main items).
 *
 * Bundle cart-edit (Phase 4 v2) is gated off at openFromCartLine — the
 * bundle short-circuit throws before reaching this module. This module
 * assumes flow === 'multistep' / standard.
 *
 * ============================================================================
 * Dependencies
 * ============================================================================
 *
 *   JRS.EventBus.on / .emit       — subscribe + publish (cart:editRequested + cart:updated)
 *   JRS.CartAPI.getCart           — snapshot + verify
 *   JRS.CartAPI.removeLinesSequential — originals / replacement revert, one change.js per line
 *   JRS.CartAPI.addItems          — re-add of replacement lines missing after removal
 *   JRS.CartAPI.readCartAuthoritative — write-path read before a miss counts
 *   JRS.CartSubmit.buildProductConfiguration — state.draft → ProductConfiguration
 *   JRS.CartSubmit.submit         — sequential POST chain (single_atc / sequential_per_size)
 *   JRS.CartSubmit.verifyAfterSubmit — heal pass + compound-key match
 *   JRS.CartPayloadBuilder.build  — preflight validation
 *   JRS.ConfigurationParser.parse — original-properties parse for diff_summary
 *   JRS.CustomizerPopup.openFromCartLine — open in edit mode
 *   JRS.CustomizerPopup.close     — exit popup
 *   JRS.CustomizerPopup.fireAnalytics — telemetry firehose
 *   JRS.CustomizerPopupState.getState — read state.draft, .editMode, .cartEditContext
 */

window.JRS = window.JRS || {};

(function (JRS) {
  'use strict';

  // ---------------------------------------------------------------------------
  // Module state
  // ---------------------------------------------------------------------------

  /** Currently-bound EventBus unsubscribe functions (init/destroy). */
  var _unsubscribers = [];

  /** Single-flight guard. Prevents double-clicks from double-firing the chain. */
  var _saveInFlight = false;

  /** Cached EventBus reference at init() time. */
  var _bus = null;

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  /**
   * Subscribe to the cart-edit EventBus channel. Idempotent — calling
   * init() multiple times unsubscribes the prior bindings first.
   */
  function init() {
    destroy();

    if (!JRS.EventBus || typeof JRS.EventBus.on !== 'function') {
      if (window.console && typeof console.warn === 'function') {
        console.warn(
          'jrs-v2-cart-edit-flow: JRS.EventBus unavailable at init. ' +
          'Tier 1 load order broken; cart-edit clicks will be silent.'
        );
      }
      return;
    }
    _bus = JRS.EventBus;

    _unsubscribers.push(_bus.on('cart:editRequested', _onEditRequested));

    // A save deferred by a 429 challenge on the previous load resumes here.
    if (_readPendingEdit()) _resumePendingEdit();
  }

  function destroy() {
    for (var i = 0; i < _unsubscribers.length; i++) {
      try {
        if (typeof _unsubscribers[i] === 'function') _unsubscribers[i]();
      } catch (err) {
        if (window.console && typeof console.warn === 'function') {
          console.warn('jrs-v2-cart-edit-flow: unsubscribe failed', err);
        }
      }
    }
    _unsubscribers = [];
    _bus = null;
  }

  // ---------------------------------------------------------------------------
  // EventBus handler — cart:editRequested
  // ---------------------------------------------------------------------------

  /**
   * Translates the trigger event into an openFromCartLine call. The
   * popup's openFromCartLine owns the heavy lifting (fetch cart, parse,
   * validate, snapshot originalProperties, dispatch OPEN_FOR_CART_EDIT).
   *
   * @param {{lineItemKey: string, lineItemQuantity?: number|null}} data
   */
  function _onEditRequested(data) {
    if (!data || typeof data !== 'object') return;
    if (!data.lineItemKey) return;

    if (!JRS.CustomizerPopup || typeof JRS.CustomizerPopup.openFromCartLine !== 'function') {
      if (window.console && typeof console.error === 'function') {
        console.error(
          'jrs-v2-cart-edit-flow: JRS.CustomizerPopup.openFromCartLine unavailable. ' +
          'Cannot open cart-edit popup.'
        );
      }
      return;
    }

    var opened = JRS.CustomizerPopup.openFromCartLine({
      lineItemKey: String(data.lineItemKey),
      lineItemQuantity: (typeof data.lineItemQuantity === 'number' ? data.lineItemQuantity : null)
    });
    // Instrumentation (2026-09-03): a rejected open used to be an unhandled
    // rejection — the Edit button silently did nothing. Record it (telemetry
    // only; the UX is unchanged this pass).
    if (opened && typeof opened.catch === 'function') {
      opened.catch(function (err) {
        try {
          if ((window.JRS && JRS.ErrorTelemetry && typeof JRS.ErrorTelemetry.report === 'function')) {
            JRS.ErrorTelemetry.report('cart_edit', 'cart_edit_open_failed', {
              silent: true,
              detail: String((err && err.message) || 'unknown'),
              line_key: String(data.lineItemKey || '')
            });
          }
        } catch (e2) {}
      });
    }
  }

  // ---------------------------------------------------------------------------
  // saveChanges — the add-before-remove orchestrator
  // ---------------------------------------------------------------------------

  /**
   * Invoke at "Update cart" CTA click.
   *
   * Returns Promise<{ok: boolean, reason?: string, error?: string, ...}>.
   * Caller (review step controller, commit 6) is responsible for setting
   * the CTA's disabled / loading state during the in-flight window.
   * This module manages the cart mutation; the controller manages the UX.
   */
  function saveChanges() {
    if (_saveInFlight) {
      return Promise.resolve({ ok: false, reason: 'in_flight' });
    }
    _saveInFlight = true;

    var state = (JRS.CustomizerPopupState && typeof JRS.CustomizerPopupState.getState === 'function')
      ? JRS.CustomizerPopupState.getState()
      : null;
    var guardStartedAt = Date.now();

    // Guard 1: popup must be in edit mode with a known clicked line.
    if (!state || !state.editMode || !state.editMode.active || !state.editMode.lineItemKey) {
      _saveInFlight = false;
      return Promise.resolve(_guardFailure('not_in_edit_mode',
        'saveChanges called when popup is not in cart-edit mode', state, guardStartedAt));
    }

    // Guard 2: must have a cart-edit context with related keys to remove.
    var ctx = state.cartEditContext;
    if (!ctx || !ctx.related_keys || ctx.related_keys.length === 0) {
      _saveInFlight = false;
      return Promise.resolve(_guardFailure('missing_edit_context',
        'cartEditContext.related_keys is empty; cannot determine which lines to remove', state, guardStartedAt));
    }

    // Guard 3: required dependencies present.
    var depCheck = _verifyDependencies();
    if (!depCheck.ok) {
      _saveInFlight = false;
      return Promise.resolve(_guardFailure('missing_dependency', depCheck.error, state, guardStartedAt));
    }

    var clickedKey = state.editMode.lineItemKey;
    var relatedKeys = ctx.related_keys.slice();
    var startedAt = Date.now();
    var editSessionId = state.editSessionId || null;
    var originalProperties = state.editMode.originalProperties || state.originalProperties || null;

    // 2026-09-14 (ClickUp 86eywgb42) — ADD-BEFORE-REMOVE. The shopper's original
    // lines are never removed until the replacement lines are proven to be in the
    // cart. Sequence: guards → snapshot → build/preflight → ADD → verify the add
    // landed (identity match, no heal) → REMOVE originals → verify removal →
    // verify (heal) → success. An ADD failure of any kind removes nothing. The
    // old remove-then-add order could leave the cart with the originals gone and
    // the replacements refused, relying on a snapshot re-add that could itself
    // fail (add_failed_rollback_failed).

    // -------------------------------------------------------------------------
    // Snapshot + payload preflight
    // -------------------------------------------------------------------------

    return JRS.CartAPI.getCart()
      .then(function (cart) {
        // Snapshot the related items. Deep-cloned so downstream cart mutations
        // can't corrupt it. Properties are strings (Shopify contract).
        var snapshot = _snapshotRelatedItems(cart, relatedKeys);
        if (snapshot.items.length === 0) {
          throw _failure('snapshot_failed',
            'No related cart lines found at snapshot time. ' +
            'Cart may have been modified in another tab.');
        }

        // Find the clicked line in the snapshot to extract product metadata for
        // the synthetic context. Fallback to the first main item.
        var clickedItem = null;
        for (var i = 0; i < snapshot.items.length; i++) {
          if (snapshot.items[i].key === clickedKey) {
            clickedItem = snapshot.items[i];
            break;
          }
        }
        if (!clickedItem) clickedItem = snapshot.mainItems[0] || snapshot.items[0];

        var syntheticContext = _buildSyntheticContext(
          clickedItem, snapshot.mainItems, state.draft, snapshot.items);

        var productConfig;
        try {
          productConfig = JRS.CartSubmit.buildProductConfiguration(state, syntheticContext);
        } catch (buildErr) {
          throw _failure('build_failed',
            (buildErr && buildErr.message) || 'Could not build cart payload');
        }

        // Preflight so invalid configurations surface BEFORE any cart write.
        var preflightPayload;
        try {
          preflightPayload = JRS.CartPayloadBuilder.build(productConfig);
        } catch (preErr) {
          throw _failure('build_failed',
            (preErr && preErr.message) || 'Invalid configuration; cart unchanged');
        }

        // Identity preflight (AGENTS.md gotcha 7). Every replacement line must carry
        // an instance id that no original carries, or the add cannot be told apart
        // from the originals (verification, partial revert, removal) and Shopify
        // could merge it into an original line. Only reachable for a standard
        // payload with zero positions, which the edit UI does not produce.
        var oldProps = (clickedItem && clickedItem.properties) || {};
        var oldIds = {
          bundleInstanceId: String(oldProps._bundle_instance_id || ''),
          customizationInstanceId: String(oldProps._customization_instance_id || '')
        };
        var preIds = _replacementIdentity(preflightPayload);
        if (!preIds.complete || _identityCollides(preIds, snapshot)) {
          throw _failure('build_failed',
            'Replacement lines have no distinct instance id; cart unchanged');
        }

        var record = {
          editSessionId: editSessionId,
          cartToken: (cart && cart.token) ? String(cart.token) : '',
          cartUpdatedAt: (cart && typeof cart._cartjsUpdatedAt === 'number') ? cart._cartjsUpdatedAt : null,
          originalKeys: relatedKeys.slice(),
          oldIds: oldIds
        };

        // -------------------------------------------------------------------
        // ADD — replacements go in first; originals untouched
        // -------------------------------------------------------------------

        return JRS.CartSubmit.submit(productConfig).then(function (submitResult) {
          var expectedPayload = (submitResult && submitResult.expectedPayload) || null;
          var newIds = _replacementIdentity(expectedPayload);

          if (!submitResult || !submitResult.ok) {
            return _handleAddFailure(submitResult, expectedPayload, newIds, record);
          }

          return _confirmReplacementsLanded(expectedPayload).then(function (landed) {
            if (landed.challenge) {
              _persistPendingEdit(record, 'add', expectedPayload, newIds);
              throw _failure('add_challenge_deferred', CHALLENGE_MESSAGE, { reloadRequired: true });
            }
            if (!landed.ok) {
              // The add answered 2xx but the replacements are not (all) readable in
              // the cart. Treat as a failed add: take out whatever did land (by the
              // replacement identity) and leave the originals alone.
              return _handleAddFailure({ ok: false, partialSuccess: true, addedItems: [],
                error: 'replacement lines not found in cart after add' }, expectedPayload, newIds, record);
            }

            // -----------------------------------------------------------------
            // REMOVE originals — only now that the replacements are proven
            // -----------------------------------------------------------------

            return _removeOriginals(record, expectedPayload, landed.cart).then(function (removal) {
              if (removal.challenge) {
                _persistPendingEdit(record, 'remove', expectedPayload, newIds);
                throw _failure('remove_challenge_deferred', CHALLENGE_MESSAGE, { reloadRequired: true });
              }
              if (!removal.ok) {
                throw _failure('remove_after_add_failed', DUPLICATE_MESSAGE, { critical: true });
              }
              if (removal.replacementsMissing > 0) {
                // Post-edit verify mismatch: the originals are gone and some
                // replacement lines are not in the cart. Raised, never rolled back.
                throw _failure('verify_failed', VERIFY_MISSING_MESSAGE, { critical: true });
              }

              // ---------------------------------------------------------------
              // Verify (heal) — runs only on the settled post-removal cart
              // ---------------------------------------------------------------

              return JRS.CartSubmit.verifyAfterSubmit(expectedPayload)
                .then(function (verifyResult) {
                  var verifyOk = !!(verifyResult && verifyResult.ok !== false);
                  var verifyMissing = (verifyResult && verifyResult.missing) || [];
                  if (!verifyOk) {
                    // Raised as telemetry; the edit itself stands (no rollback).
                    _fireCartEditFailed(_failure('verify_failed',
                      verifyMissing.length + ' expected line(s) unmatched after heal'), state, startedAt, editSessionId);
                  }

                  var diff = _safeComputeDiffSummary(snapshot, state.draft);
                  var msInSession = Date.now() - (state.openedAt || startedAt);
                  var addedItems = submitResult.addedItems || [];

                  _fireLineEdited({
                    edit_session_id: editSessionId,
                    original_properties_snapshot: _structuredProperties(originalProperties),
                    new_properties: _structuredProperties(_firstParentProperties(addedItems)),
                    diff_summary: diff,
                    cart_submit_pattern: 'add_then_remove',
                    // 2026-09-14 — which cart entry point opened this session:
                    // 'cart_add_logo' ("Add your logo" on a plain line) or 'cart_edit'.
                    edit_entry_point: (ctx && ctx.entry_point) || 'cart_edit',
                    internal_submit_pattern: submitResult.pattern || 'unknown',
                    total_qty: _sumQuantities(addedItems),
                    total_value: 0,    // Phase 5 polish — wire from CartPayloadBuilder pricing math
                    currency: 'GBP',   // Phase 5 polish — read from /cart.js when value is wired
                    ms_in_edit_session: msInSession,
                    size_variant_count: (state.draft && state.draft.sizes && state.draft.sizes.length) || 1,
                    verify_ok: verifyOk,
                    verify_missing_count: verifyMissing.length
                  });

                  if (_bus && typeof _bus.emit === 'function') {
                    _bus.emit('cart:updated', { reason: 'cart_edit' });
                  }

                  if (JRS.CustomizerPopup && typeof JRS.CustomizerPopup.close === 'function') {
                    JRS.CustomizerPopup.close({
                      reason: 'cart_edit_save_success',
                      force: true,
                      isConfirm: true
                    });
                  }

                  _saveInFlight = false;
                  return {
                    ok: true,
                    addedItems: addedItems,
                    verifyOk: verifyOk,
                    pattern: submitResult.pattern || 'unknown'
                  };
                });
            });
          });
        });
      })
      .catch(function (err) {
        _saveInFlight = false;
        var failure = (err && err._cartEditFailure) ? err : _failure(
          'unknown',
          (err && err.message) || String(err)
        );
        _fireCartEditFailed(failure, state, startedAt, editSessionId);
        return _failureResult(failure._reason, failure.message, {
          critical: !!failure._critical,
          rollbackOk: failure._rollbackOk,
          reloadRequired: !!failure._reloadRequired
        });
      });
  }

  var CHALLENGE_MESSAGE = 'Your connection is being verified. Please reload the page — ' +
    'we\'ll finish updating your cart when it loads.';
  var VERIFY_MISSING_MESSAGE = 'Your cart was updated, but part of your item may be missing. ' +
    'Please reload the page and check your cart before checkout.';
  var DUPLICATE_MESSAGE = 'Your updated item was added, but we couldn\'t remove the original, ' +
    'so it is in your cart twice. Please reload the page and remove the duplicate before checkout.';

  // ---------------------------------------------------------------------------
  // Add-before-remove steps
  // ---------------------------------------------------------------------------

  /**
   * ADD failed (any status). Nothing original has been touched. On a challenge no
   * further cart request is made this turn: the edit is persisted and the shopper
   * asked to reload. Otherwise any replacement lines that did land are removed by
   * the replacement instance id (never by _lineItemKey) and the originals stay.
   */
  function _handleAddFailure(submitResult, expectedPayload, newIds, record) {
    var addError = (submitResult && (submitResult.shopifyDescription || submitResult.error)) || 'Add failed';
    if (submitResult && submitResult.challenge) {
      _persistPendingEdit(record, 'add', expectedPayload, newIds);
      return Promise.reject(_failure('add_challenge_deferred', CHALLENGE_MESSAGE, { reloadRequired: true }));
    }
    var mayHaveLanded = !!(submitResult && (submitResult.partialSuccess ||
      (submitResult.addedItems && submitResult.addedItems.length > 0)));
    if (!mayHaveLanded) {
      return Promise.reject(_failure('add_failed_no_mutation', addError));
    }
    return _revertReplacements(newIds).then(function (revert) {
      if (revert.challenge) {
        _persistPendingEdit(record, 'add', expectedPayload, newIds);
        throw _failure('add_challenge_deferred', CHALLENGE_MESSAGE, { reloadRequired: true });
      }
      if (!revert.ok) {
        throw _failure('partial_add_revert_failed',
          'Part of your update was added and could not be taken out again. ' +
          'Please reload the page and check your cart before checkout.', { critical: true });
      }
      // CartSubmit's partial-add accounting matches children by _lineItemKey, which
      // the originals share; only a line found by the replacement identity counts.
      if (revert.removed === 0) throw _failure('add_failed_no_mutation', addError);
      throw _failure('partial_add_reverted', addError);
    });
  }

  /**
   * Heal-free check that every replacement line is in the cart, matched by instance
   * identity. A cached /cart.js miss gets one write-path read before it counts.
   * Deliberately NOT verifyAfterSubmit: its CartValidator heal would run against a
   * cart that transiently holds originals AND replacements.
   */
  function _confirmReplacementsLanded(expectedPayload) {
    var expected = (expectedPayload && expectedPayload.items) || [];
    return JRS.CartAPI.getCart().then(function (cart) {
      if (_missingByIdentity(expected, cart).length === 0) return { ok: true, cart: cart };
      if (typeof JRS.CartAPI.readCartAuthoritative !== 'function') return { ok: false, cart: cart };
      return JRS.CartAPI.readCartAuthoritative().then(function (authCart) {
        return { ok: _missingByIdentity(expected, authCart).length === 0, cart: authCart };
      });
    }).catch(function (err) {
      return { ok: false, challenge: !!(err && err.challenge), cart: null };
    });
  }

  /**
   * Remove the original lines, then verify by identity that no original remains
   * and every replacement is present.
   *
   * 2026-09-26 (ClickUp 86eywgb42): originals go ONE /cart/change.js per line
   * (CartAPI.removeLinesSequential), never the batch /cart/update.js hash. With
   * originals and replacements in the cart together, the batch zeroed replacement
   * lines of the same variant and left originals behind (reproduced on the BYOB
   * preview theme, 3 of 3 runs). GA4 export 2026-09-15 to 09-24: 225 critical
   * verify_failed and 48 remove_after_add_failed.
   *
   * Any non-challenge failure gets one retry from a fresh write-path read. Once no
   * original remains, replacement lines still missing are re-added (exactly those
   * lines, matched by instance identity). This cannot duplicate: the originals are
   * gone and a line found by identity is never re-added.
   *
   * @returns {Promise<{ok, challenge, replacementsMissing, repaired, cart}>}
   */
  function _removeOriginals(record, expectedPayload, cartBefore) {
    var expected = (expectedPayload && expectedPayload.items) || [];

    function attempt(cart) {
      return JRS.CartAPI.removeLinesSequential(function (item) {
        return _isOriginalLine(record, item);
      }, { cart: cart });
    }

    function verify(result) {
      if (result && result.challenge) return { ok: false, challenge: true };
      if (!result || !result.ok) return { ok: false, challenge: false, httpStatus: (result && result.httpStatus) || 0 };
      var cart = result.cart;
      if (_originalsRemaining(record, cart) > 0) return { ok: false, challenge: false, httpStatus: 0 };
      return { ok: true, cart: cart, missing: _missingByIdentity(expected, cart) };
    }

    return attempt(cartBefore).then(verify).then(function (first) {
      if (first.ok || first.challenge) return first;
      return attempt(null).then(verify);
    }).then(function (out) {
      if (!out.ok) return out;
      if (out.missing.length === 0) return { ok: true, cart: out.cart, replacementsMissing: 0, repaired: 0 };
      var toAdd = out.missing;
      return JRS.CartAPI.addItems({ items: toAdd }).then(function (addRes) {
        if (addRes && addRes.challenge) return { ok: true, cart: out.cart, replacementsMissing: toAdd.length, repaired: 0 };
        return JRS.CartAPI.readCartAuthoritative().then(function (authCart) {
          var stillMissing = _missingByIdentity(expected, authCart).length;
          return { ok: true, cart: authCart, replacementsMissing: stillMissing, repaired: toAdd.length - stillMissing };
        });
      });
    }).catch(function (err) {
      return { ok: false, challenge: !!(err && err.challenge) };
    });
  }

  /** Remove every line carrying a replacement instance id, one change.js per line. */
  function _revertReplacements(newIds) {
    if (!newIds.bundleInstanceId && !newIds.customizationInstanceId) {
      return Promise.resolve({ ok: false, challenge: false, removed: 0 });
    }
    // 2026-09-26: not removeItemsByInstance (one batch update.js). The originals
    // are still in the cart here and share variants with the replacements, so a
    // batch could zero an original instead (see _removeOriginals).
    return JRS.CartAPI.removeLinesSequential(function (item) {
      var p = (item && item.properties) || {};
      return !!((newIds.bundleInstanceId && String(p._bundle_instance_id || '') === newIds.bundleInstanceId) ||
        (newIds.customizationInstanceId && String(p._customization_instance_id || '') === newIds.customizationInstanceId));
    }).then(function (res) {
      return { ok: !!(res && res.ok), challenge: !!(res && res.challenge), removed: (res && res.removed) || 0 };
    }).catch(function (err) {
      return { ok: false, challenge: !!(err && err.challenge), removed: 0 };
    });
  }

  /**
   * The instance ids a payload's lines carry. `complete` is false when any line has
   * none, or the payload spans more than one instance of a kind.
   */
  function _replacementIdentity(payload) {
    var items = (payload && payload.items) || [];
    var bundle = {}, custom = {}, complete = items.length > 0;
    for (var i = 0; i < items.length; i++) {
      var p = (items[i] && items[i].properties) || {};
      var b = String(p._bundle_instance_id || '');
      var c = String(p._customization_instance_id || '');
      if (b) bundle[b] = true;
      if (c) custom[c] = true;
      if (!b && !c) complete = false;
    }
    var bKeys = Object.keys(bundle), cKeys = Object.keys(custom);
    if (bKeys.length > 1 || cKeys.length > 1) complete = false;
    return { bundleInstanceId: bKeys[0] || '', customizationInstanceId: cKeys[0] || '', complete: complete };
  }

  function _identityCollides(ids, snapshot) {
    var items = (snapshot && snapshot.items) || [];
    for (var i = 0; i < items.length; i++) {
      var p = items[i].properties || {};
      if (ids.bundleInstanceId && String(p._bundle_instance_id || '') === ids.bundleInstanceId) return true;
      if (ids.customizationInstanceId && String(p._customization_instance_id || '') === ids.customizationInstanceId) return true;
    }
    return false;
  }

  /**
   * Expected lines with no cart line of the same variant carrying the same instance
   * id and _lineItemKey. Instance id is required: _lineItemKey alone is shared by
   * the original and replacement children (and by other bundles of the same
   * product), so it can never prove a replacement landed.
   */
  function _missingByIdentity(expectedItems, cart) {
    var observed = (cart && cart.items) || [];
    var missing = [];
    for (var i = 0; i < expectedItems.length; i++) {
      var exp = expectedItems[i] || {};
      var ep = exp.properties || {};
      var found = false;
      for (var j = 0; j < observed.length && !found; j++) {
        var obs = observed[j] || {};
        var op = obs.properties || {};
        if (Number(obs.variant_id || obs.id) !== Number(exp.id)) continue;
        if (String(op._bundle_instance_id || '') !== String(ep._bundle_instance_id || '')) continue;
        if (String(op._customization_instance_id || '') !== String(ep._customization_instance_id || '')) continue;
        if (String(op._lineItemKey || '') !== String(ep._lineItemKey || '')) continue;
        if (String(op._ProductSize || '') !== String(ep._ProductSize || '')) continue;
        if (String(op._ProductVariantId || '') !== String(ep._ProductVariantId || '')) continue;
        found = true;
      }
      if (!found) missing.push(exp);
    }
    return missing;
  }

  /** An original line: snapshot key or original instance id. */
  function _isOriginalLine(record, item) {
    var p = (item && item.properties) || {};
    var ids = record.oldIds || {};
    return record.originalKeys.indexOf(item && item.key) !== -1 ||
      !!(ids.bundleInstanceId && String(p._bundle_instance_id || '') === ids.bundleInstanceId) ||
      !!(ids.customizationInstanceId && String(p._customization_instance_id || '') === ids.customizationInstanceId);
  }

  /** Lines still in the cart that are originals: snapshot key or original instance id. */
  function _originalsRemaining(record, cart) {
    var n = 0;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (_isOriginalLine(record, items[i])) n++;
    }
    return n;
  }

  // ---------------------------------------------------------------------------
  // Challenge deferral — persist, reload, resume on next load
  // ---------------------------------------------------------------------------

  var PENDING_EDIT_KEY = 'jrs:cartEdit:pending:v1';
  var PENDING_EDIT_MAX_AGE_MS = 30 * 60 * 1000;

  /**
   * A 429 challenge blocks every request until a page navigation clears it, so no
   * further cart request is made in the turn that saw it. The edit is written to
   * sessionStorage with the cart token it was made against; the next load resumes
   * it (see _resumePendingEdit) or discards it when the cart is not the same cart.
   */
  function _persistPendingEdit(record, stage, expectedPayload, newIds) {
    try {
      var expected = ((expectedPayload && expectedPayload.items) || []).map(function (it) {
        var p = (it && it.properties) || {};
        return { id: it.id, quantity: it.quantity, properties: {
          _bundle_instance_id: p._bundle_instance_id || '',
          _customization_instance_id: p._customization_instance_id || '',
          _lineItemKey: p._lineItemKey || '',
          _ProductSize: p._ProductSize || '',
          _ProductVariantId: p._ProductVariantId || ''
        } };
      });
      window.sessionStorage.setItem(PENDING_EDIT_KEY, JSON.stringify({
        v: 1,
        stage: stage,
        savedAt: Date.now(),
        editSessionId: record.editSessionId || null,
        cartToken: record.cartToken || '',
        cartUpdatedAt: record.cartUpdatedAt,
        originalKeys: record.originalKeys,
        oldIds: record.oldIds,
        newIds: { bundleInstanceId: newIds.bundleInstanceId, customizationInstanceId: newIds.customizationInstanceId },
        expectedItems: expected
      }));
    } catch (e) {}
  }

  function _readPendingEdit() {
    try {
      var raw = window.sessionStorage.getItem(PENDING_EDIT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function _clearPendingEdit() {
    try { window.sessionStorage.removeItem(PENDING_EDIT_KEY); } catch (e) {}
  }

  /**
   * Next-load resume of a challenge-deferred edit. Never re-issues the add. If every
   * replacement is in the cart, finish the edit by removing the originals; if only
   * some are, take those out and leave the originals; if none are, nothing changed.
   * Discarded without a cart write when stale, malformed, or the cart token differs.
   *
   * @returns {Promise<{status: string}>}
   */
  function _resumePendingEdit() {
    var rec = _readPendingEdit();
    if (!rec) return Promise.resolve({ status: 'none' });
    _clearPendingEdit();
    if (rec.v !== 1 || !rec.cartToken || !rec.expectedItems || !rec.expectedItems.length ||
        !rec.newIds || !(rec.newIds.bundleInstanceId || rec.newIds.customizationInstanceId) ||
        !(Date.now() - Number(rec.savedAt || 0) < PENDING_EDIT_MAX_AGE_MS) ||
        !JRS.CartAPI || typeof JRS.CartAPI.getCart !== 'function') {
      return Promise.resolve({ status: 'discarded' });
    }
    var record = { editSessionId: rec.editSessionId, cartToken: rec.cartToken, cartUpdatedAt: rec.cartUpdatedAt,
      originalKeys: rec.originalKeys || [], oldIds: rec.oldIds || {} };
    var expectedPayload = { items: rec.expectedItems };
    var failState = { editSessionId: rec.editSessionId };

    function done(status, failure) {
      if (failure) _fireCartEditFailed(failure, failState, null, rec.editSessionId);
      if (failure && _bus && typeof _bus.emit === 'function') {
        _bus.emit('cart:editBlocked', { message: failure.message, reason: failure._reason });
      }
      if (status !== 'discarded' && status !== 'nothing_landed' && _bus && typeof _bus.emit === 'function') {
        // 'cart_edit' makes the cart page reload onto the finished edit; a failed
        // resume keeps the page (and the notice) and only refreshes totals.
        _bus.emit('cart:updated', { reason: status === 'completed' ? 'cart_edit' : 'cart_edit_resume' });
      }
      return { status: status };
    }

    return JRS.CartAPI.getCart().then(function (cart) {
      if (!cart || String(cart.token || '') !== rec.cartToken) return { status: 'discarded' };
      var missing = _missingByIdentity(rec.expectedItems, cart).length;
      if (missing === rec.expectedItems.length) return done('nothing_landed');
      if (missing > 0) {
        return _revertReplacements(rec.newIds).then(function (revert) {
          if (!revert.ok) {
            if (revert.challenge) _persistPendingEdit(record, 'add', expectedPayload, rec.newIds);
            return done('revert_failed', _failure('partial_add_revert_failed',
              'Part of your update was added and could not be taken out again. ' +
              'Please check your cart before checkout.', { critical: true }));
          }
          return done('reverted', _failure('partial_add_reverted',
            'We couldn\'t finish updating your item, so your cart is back to how it was.'));
        });
      }
      return _removeOriginals(record, expectedPayload, cart).then(function (removal) {
        if (removal.challenge) {
          _persistPendingEdit(record, 'remove', expectedPayload, rec.newIds);
          return done('deferred_again');
        }
        if (!removal.ok) {
          return done('remove_failed', _failure('remove_after_add_failed', DUPLICATE_MESSAGE, { critical: true }));
        }
        if (removal.replacementsMissing > 0) {
          return done('verify_failed', _failure('verify_failed', VERIFY_MISSING_MESSAGE, { critical: true }));
        }
        return done('completed');
      });
    }).catch(function (err) {
      // Still challenged (or unreadable): keep the record for the next load.
      if (err && err.challenge) _persistPendingEdit(record, rec.stage, expectedPayload, rec.newIds);
      return { status: 'read_failed' };
    });
  }

  // ---------------------------------------------------------------------------
  // cancel — clean popup close (no cart mutation)
  // ---------------------------------------------------------------------------

  /**
   * Customer clicked the Cancel changes link in the edit banner. Popup
   * was operating on a working copy in state.draft; the cart was never
   * touched. Close = throw away the working copy.
   *
   * Per Phase 4 plan §2-Q4 reconciliation: cancel is a no-op on cart
   * state. Distinct from rollback (which is a failure-recovery path).
   *
   * Telemetry (`pdp_customizer_cart_edit_cancelled`) is fired by the
   * review controller in commit 6 BEFORE calling this function, so this
   * function only owns the cart-state / popup-close concern.
   */
  function cancel() {
    if (!JRS.CustomizerPopup || typeof JRS.CustomizerPopup.close !== 'function') return;
    JRS.CustomizerPopup.close({
      reason: 'cart_edit_banner_cancel',
      force: true
    });
  }

  // ---------------------------------------------------------------------------
  // Snapshot helpers
  // ---------------------------------------------------------------------------

  /**
   * Build a deep-cloned snapshot of the cart items matching the related
   * keys. Separates main items (no _ParentVariant) from children (has
   * _ParentVariant) so callers can find product metadata efficiently.
   *
   * @param {Object} cart — /cart.js response
   * @param {string[]} relatedKeys
   * @returns {{items: Array, mainItems: Array, childItems: Array}}
   */
  function _snapshotRelatedItems(cart, relatedKeys) {
    var allItems = (cart && cart.items) || [];
    var keySet = {};
    for (var i = 0; i < relatedKeys.length; i++) keySet[relatedKeys[i]] = true;

    var items = [];
    var mainItems = [];
    var childItems = [];

    for (var j = 0; j < allItems.length; j++) {
      var item = allItems[j];
      if (!item || !item.key) continue;
      if (!keySet[item.key]) continue;
      var cloned = _deepClone(item);
      items.push(cloned);
      var isChild = !!(cloned.properties && cloned.properties._ParentVariant);
      if (isChild) {
        childItems.push(cloned);
      } else {
        mainItems.push(cloned);
      }
    }
    return { items: items, mainItems: mainItems, childItems: childItems };
  }

  // ---------------------------------------------------------------------------
  // Synthetic-context construction
  // ---------------------------------------------------------------------------

  /**
   * Read product tags embedded on the cart row by cart-item.liquid
   * (`data-jrs-product-tags`, JSON array). Returns [] when the row is
   * missing or the attribute is unparseable.
   *
   * @param {string} lineItemKey
   * @returns {string[]}
   */
  function _readProductTagsFromDom(lineItemKey) {
    if (!lineItemKey || typeof document === 'undefined') return [];
    var row = null;
    var nodes = document.querySelectorAll('[data-jrs-cart-item][data-jrs-product-tags]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].getAttribute('data-jrs-item-key') === lineItemKey) {
        row = nodes[i];
        break;
      }
    }
    if (!row) return [];
    var raw = row.getAttribute('data-jrs-product-tags');
    if (!raw) return [];
    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_e) {
      return [];
    }
  }

  /**
   * For bundle lines, method restrictions (hide-embroidery / mix-and-match)
   * apply to the bundle PARENT product's tags, not a child garment's.
   *
   * @param {Object} clickedItem
   * @param {Array} cartItems
   * @returns {string[]}
   */
  function _resolveProductTagsForCartLine(clickedItem, cartItems) {
    var cp = (clickedItem && clickedItem.properties) || {};
    var tagLine = clickedItem;
    if (!cp._bundle_raw && cp._bundle_instance_id && cartItems) {
      for (var i = 0; i < cartItems.length; i++) {
        var p = (cartItems[i] && cartItems[i].properties) || {};
        if (p._bundle_raw && p._bundle_instance_id === cp._bundle_instance_id) {
          tagLine = cartItems[i];
          break;
        }
      }
    }
    return _readProductTagsFromDom(tagLine && tagLine.key);
  }

  /**
   * Read the per-category branding meta (data-jrs-bundle-category-meta) off the
   * bundle-PARENT cart row — the bundle metaobject's per-category child `tags`
   * (method restrictions) + `positionHandles` (which positions the garment offers),
   * re-emitted by cart-item.liquid so per_product cart-edit reproduces the PDP.
   * Resolves the parent line the same way _resolveProductTagsForCartLine does (a
   * child-clicked bundle still finds its parent). Returns [] on any miss.
   *
   * @param {Object} clickedItem
   * @param {Array} cartItems
   * @returns {Array<{index:number, productHandles:string[], tags:string[], positionHandles:string[]}>}
   */
  function _readBundleCategoryMetaForCartLine(clickedItem, cartItems) {
    var cp = (clickedItem && clickedItem.properties) || {};
    var tagLine = clickedItem;
    if (!cp._bundle_raw && cp._bundle_instance_id && cartItems) {
      for (var i = 0; i < cartItems.length; i++) {
        var p = (cartItems[i] && cartItems[i].properties) || {};
        if (p._bundle_raw && p._bundle_instance_id === cp._bundle_instance_id) {
          tagLine = cartItems[i];
          break;
        }
      }
    }
    return _readBundleCategoryMetaFromDom(tagLine && tagLine.key);
  }

  function _readBundleCategoryMetaFromDom(lineItemKey) {
    if (!lineItemKey || typeof document === 'undefined') return [];
    var nodes = document.querySelectorAll('[data-jrs-cart-item][data-jrs-bundle-category-meta]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].getAttribute('data-jrs-item-key') === lineItemKey) {
        var raw = nodes[i].getAttribute('data-jrs-bundle-category-meta');
        if (!raw) return [];
        try {
          var parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? parsed : [];
        } catch (_e) {
          return [];
        }
      }
    }
    return [];
  }

  /**
   * Match a parser-reconstructed bundle category to its emitted meta entry by
   * productHandle intersection (the child's configured products ∩ the meta
   * category's products[].handle). Robust to category-index drift; no index
   * fallback — a wrong index match could mis-scope tags/positions, so an unmatched
   * category fails open (caller uses [] defaults) instead. Returns the meta entry
   * or null.
   */
  function _matchCategoryMeta(categoryMeta, productHandles) {
    if (!categoryMeta || !categoryMeta.length) return null;
    var handles = productHandles || [];
    for (var i = 0; i < categoryMeta.length; i++) {
      var m = categoryMeta[i];
      var prods = (m && m.products) || [];
      for (var p = 0; p < prods.length; p++) {
        var ph = prods[p] && prods[p].handle;
        if (!ph) continue;
        for (var h = 0; h < handles.length; h++) {
          if (handles[h] && handles[h] === ph) return m;
        }
      }
    }
    return null;
  }

  /**
   * The product tags to apply for a bundle category, scoped to the products this
   * cart line actually configured (childProducts) — NOT a union of every candidate
   * in the metaobject category. This matters both ways: a union would over-restrict
   * the per_product picker AND, because bundle_wide save re-reads children[].tags
   * for per-child method forcing, a union could flip a paid line's method to satisfy
   * a candidate garment the customer never chose. Scoping to childProducts keeps the
   * forcing idempotent with the original add. Deduped, order-preserving. [] when no
   * configured product matches (fail open — no restriction, never a wrong one).
   */
  function _childTagsForCategory(metaCat, childProducts) {
    if (!metaCat || !metaCat.products) return [];
    var wanted = {};
    var kids = childProducts || [];
    for (var i = 0; i < kids.length; i++) { if (kids[i]) wanted[kids[i]] = true; }
    var out = [];
    var seen = {};
    for (var p = 0; p < metaCat.products.length; p++) {
      var mp = metaCat.products[p];
      if (!mp || !Object.prototype.hasOwnProperty.call(wanted, mp.handle)) continue;
      var t = mp.tags || [];
      for (var k = 0; k < t.length; k++) {
        if (!Object.prototype.hasOwnProperty.call(seen, t[k])) { seen[t[k]] = true; out.push(t[k]); }
      }
    }
    return out;
  }

  /**
   * Hydrate window.JRS_PDP_CONTEXT for a cart-edit session so step
   * controllers (position picker, application_method) can apply the same
   * hide-embroidery / hide-print / mix-and-match rules as the PDP flow.
   *
   * @param {Object} clickedItem
   * @param {Array} mainItems
   * @param {Object} draft
   * @param {Array} allCartItems
   */
  function _hydratePdpContextForCartEdit(clickedItem, mainItems, draft, allCartItems) {
    if (typeof window === 'undefined') return;
    var synthetic = _buildSyntheticContext(clickedItem, mainItems, draft, allCartItems);
    window.JRS_PDP_CONTEXT = Object.assign({}, window.JRS_PDP_CONTEXT || {}, synthetic);
  }

  /**
   * Construct a context object shaped like window.JRS_PDP_CONTEXT from
   * the clicked cart-line data + the union of main items' variants.
   *
   * JRS.CartSubmit.buildProductConfiguration's multistep path reads:
   *   ctx.productId, ctx.productHandle, ctx.productTitle, ctx.productImage,
   *   ctx.productColour, ctx.sizes, ctx.productVariantId
   *
   * JRS.CartSubmit.buildProductConfiguration's bundle path additionally reads:
   *   ctx.bundleProductVariantId, ctx.bundleProductHandle, ctx.bundleMetaobjectId
   *   (Doc 2 §13.13 — see docs/phase-3-bundle-variant-id-diagnosis.md
   *   for the equivalent fix in jrs-v2-step-review.js:_resolvePageContext
   *   that this function mirrors for Phase 4 v2 readiness.)
   *
   * For cart-edit, sizes come from state.draft.sizes (which the parser
   * populated from the union of mainItems at popup-open time). The
   * clicked item supplies product metadata.
   *
   * @param {Object} clickedItem
   * @param {Array} mainItems
   * @param {Object} draft - state.draft
   * @param {Array} [allCartItems] - full cart lines for bundle-parent tag lookup
   */
  function _buildSyntheticContext(clickedItem, mainItems, draft, allCartItems) {
    var props = (clickedItem && clickedItem.properties) || {};

    // Sizes: use state.draft.sizes (parser-populated). The parser includes
    // {variantId, sizeLabel, quantity, sku} per main item. If draft.sizes
    // is empty for some reason, fall back to deriving from mainItems.
    var sizes = (draft && draft.sizes && draft.sizes.length > 0)
      ? draft.sizes
      : _deriveSizesFromMains(mainItems);

    var productImage = '';
    if (clickedItem && clickedItem.featured_image && clickedItem.featured_image.url) {
      productImage = clickedItem.featured_image.url;
    } else if (clickedItem && clickedItem.image) {
      productImage = clickedItem.image;
    }

    // Colour: cart line items typically expose `options_with_values` (an
    // array of {name, value}). If a "Colour"/"Color" option exists, use
    // its value. Fall back to empty so the builder uses '' (cart-payload
    // builder handles that gracefully).
    var productColour = _extractColourOption(clickedItem) || (props._ProductColour || '');

    // Bundle field forwarding. Phase 3 commit 3 (2026-05-21) added the
    // initial defensive plumbing from clickedItem; Phase 4 v2 Session 2
    // (this commit) extends it to read parser-supplied draft._bundle_meta
    // + draft._bundle_children when present.
    //
    // Detection: clicked item carries _bundle_raw (Doc 2 §6.1 marker),
    // OR the parser (Session 1's _parseBundle) emitted _bundle_meta on
    // the draft. Either signal triggers bundle context construction.
    // Multistep cart-edit (no _bundle_raw, no _bundle_meta) falls
    // through to defaults — identical to the previous behaviour. Zero
    // risk to the live multistep cart-edit flow.
    var isBundleParent = !!(props && props._bundle_raw);
    var hasBundleMeta = !!(draft && draft._bundle_meta);
    var bundleMode = isBundleParent || hasBundleMeta;

    // Prefer parser-supplied _bundle_meta values when available — they
    // went through the parser's dedicated extraction (handle from
    // _bundle URL, instance id from _bundle_instance_id). Fall back to
    // the clicked item's variant_id + handle (Phase 3's defensive
    // plumbing) when meta is absent. Both should produce identical
    // values on a well-formed bundle cart line; the parser path is
    // canonical and the fallback is belt-and-braces.
    var bundleMeta = (draft && draft._bundle_meta) || null;
    var bundleProductVariantId = 0;
    var bundleProductHandle = '';
    if (bundleMeta) {
      bundleProductVariantId = bundleMeta.bundleProductVariantId || 0;
      bundleProductHandle = bundleMeta.bundleProductHandle || '';
    } else if (isBundleParent) {
      bundleProductVariantId = (clickedItem && (clickedItem.variant_id || clickedItem.id)) || 0;
      bundleProductHandle = (clickedItem && clickedItem.handle) || '';
    }

    // bundle_children: translate the parser's _bundle_children (state.
    // draft shape) into the ctx.bundle_children shape that cart-submit's
    // _buildBundleCategories expects (jrs-v2-cart-submit.js:147-168).
    // The parser emits productHandle + colour on size entries; cart-
    // submit reads them at child level. Flatten the first size's
    // productHandle/colour up to the child so the cart-payload-builder
    // emits non-empty _BundleCategoryProducts and _ProductColor.
    var bundleChildrenCtx = null;
    var bundleCategoriesCtx = null;
    if (bundleMode && draft && draft._bundle_children && draft._bundle_children.length > 0) {
      // Per-category branding meta (child tags + position handles) from the bundle
      // metaobject, re-emitted on the bundle-parent cart row. Drives the picker's
      // per-child method restrictions + per-garment position hiding in cart-edit.
      var _catMeta = _readBundleCategoryMetaForCartLine(clickedItem, allCartItems || mainItems || []);
      bundleChildrenCtx = _translateBundleChildrenForContext(draft._bundle_children, _catMeta);
      // Phase per_product S4 — reconstruct the per_product category list
      // (categoryIndex + name + handles) from the parser's _bundle_children so
      // a per_product cart-edit SAVE preserves category names in _logoproduct N
      // / _BundleItemName (cart-submit's _buildPerProductBundleCategories reads
      // ctx.bundle_categories for names). Benign for bundle_wide cart-edit —
      // cart-submit only reads bundle_categories on the per_product branch.
      // Also seeds Blob A (positions[]) from the cart line's `_bundle_raw` so a
      // re-save preserves each position's SAVED pricing variant (e.g. the FREE
      // Left Breast) instead of re-deriving the paid shop variant.
      bundleCategoriesCtx = _reconstructBundleCategoriesForContext(draft._bundle_children, _catMeta, props._bundle_meta || props._bundle_raw); // meta-first (2026-08-20)
    }

    return {
      productId: (clickedItem && (clickedItem.product_id || clickedItem.productId)) || 0,
      productHandle: (clickedItem && clickedItem.handle) || '',
      productTitle: (clickedItem && clickedItem.product_title) || (clickedItem && clickedItem.title) || '',
      productImage: productImage,
      productColour: productColour,
      // Display-only (product-card meta): the cart line's own Size option + line
      // quantity. Used by the review product card when ctx.sizes is empty in
      // cart-edit. NOT read by the cart-payload builder (save reads sizes /
      // bundle_children), so purely cosmetic — no save-path impact.
      productSize: _extractSizeOption(clickedItem),
      lineItemQuantity: (clickedItem && typeof clickedItem.quantity === 'number') ? clickedItem.quantity : null,
      productPricing: null,
      sizes: sizes,
      productVariantId: (clickedItem && (clickedItem.variant_id || clickedItem.id)) || (sizes[0] && sizes[0].variantId) || 0,
      bundle_children: bundleChildrenCtx,
      bundle_categories: bundleCategoriesCtx,
      bundleProductVariantId: bundleProductVariantId,
      bundleProductHandle: bundleProductHandle,
      // bundleMetaobjectId stays '' because the cart line doesn't carry
      // it (it's a Liquid-time identifier and the parser couldn't
      // extract it from cart properties either). Cart-submit's bundle
      // path doesn't read this field for variant resolution; it's
      // documentation metadata that travels through to the cart payload.
      bundleMetaobjectId: '',
      // Method restrictions (hide-embroidery / hide-print / mix-and-match)
      // read JRS_PDP_CONTEXT.productTags in the position picker +
      // application_method step. Cart /cart.js lines don't carry tags,
      // so we read them from the cart-row DOM attribute at open/save time.
      productTags: _resolveProductTagsForCartLine(
        clickedItem,
        allCartItems || mainItems || []
      )
    };
  }

  /**
   * Translate the parser's _bundle_children (state.draft shape, sizes
   * carry product-level details) into the ctx.bundle_children shape
   * cart-submit's _buildBundleCategories expects (child-level
   * productHandle / colour, sizes carry only size-level fields).
   *
   * Phase 4 v2 Session 2. Parser-emitted shape per Session 1:
   *   { categoryName, categoryIndex, productCount, products: [<handle>],
   *     sizes: [{variantId, sizeLabel, quantity, sku, colour, productHandle}] }
   *
   * Cart-submit-expected shape per cart-submit.js:147-168:
   *   { categoryName, productId, productHandle, productTitle, colour,
   *     imageUrl, sizes: [{size, quantity, variantId, sku}] }
   *
   * Fields not recoverable from cart data — productId, productTitle,
   * imageUrl — default to 0/''. They're not read by getBundleVariantId
   * or the cart-payload-builder's per-child enrichment (which only
   * needs productHandle and colour from product-level), so the
   * defaults are benign for the save flow. A future Phase 5 polish
   * item could backfill these via Storefront API lookup if cart-edit
   * UX surfaces product imagery.
   *
   * @param {Array<object>} parserBundleChildren
   * @returns {Array<object>} cart-submit-shape bundle_children
   */
  function _translateBundleChildrenForContext(parserBundleChildren, categoryMeta) {
    var out = [];
    for (var i = 0; i < parserBundleChildren.length; i++) {
      var child = parserBundleChildren[i] || {};
      var primarySize = (child.sizes && child.sizes[0]) || {};
      var fallbackHandle = (child.products && child.products[0]) || '';
      var meta = _matchCategoryMeta(categoryMeta, child.products);
      out.push({
        categoryName: child.categoryName || '',
        productId: 0,
        productHandle: primarySize.productHandle || fallbackHandle,
        // Per-child product tags (hide-* / remove-{pos}-{method}) for the picker's
        // method restrictions (jrs-v2-step-position-picker _activeCategoryChildTags).
        // Cart-line data doesn't carry them, so they're re-emitted on the cart row
        // from the bundle metaobject, scoped to the CHOSEN product(s) this line
        // configured (not a candidate union — see _childTagsForCategory). [] when
        // unmatched → no restriction (fail open). Save-safe: per_product save reads
        // category_configs; bundle_wide save re-reads these but chosen-product tags
        // re-force idempotently with the original add.
        tags: _childTagsForCategory(meta, child.products),
        // Carry the real product name + garment image the parser captured from
        // _bundle_raw, so the rebuilt _bundle_raw (buildBundleRawFromConfig →
        // products.{}.name/image) keeps them and the cart manifest doesn't blank
        // out after a per_product save.
        productTitle: child.productName || '',
        colour: primarySize.colour || '',
        imageUrl: child.imageUrl || '',
        sizes: (child.sizes || []).map(function (s) {
          return {
            size: s.sizeLabel || '',
            quantity: s.quantity || 0,
            variantId: s.variantId || 0,
            sku: s.sku || '',
            // Per-size colour. A category can mix colours across sizes (e.g.
            // Burgundy 7XL + Bottle Green S). The parser captures it per
            // (colour, size) variant; carry it through so the builder stamps the
            // correct _ProductColor per size (via _normaliseSizes) instead of
            // collapsing every size to the category's first colour on cart-edit save.
            colour: s.colour || ''
          };
        })
      });
    }
    return out;
  }

  /**
   * Reconstruct a per_product category list (categoryIndex + name +
   * productHandles) from the parser's _bundle_children, for the cart-edit SAVE
   * context. cart-submit's _buildPerProductBundleCategories reads
   * ctx.bundle_categories[K].name for _logoproduct N / _BundleItemName.
   *
   * positions[] is SEEDED from `_bundle_raw` (when passed) so cart-submit
   * re-derives each position's SAVED pricing variant — preserving a bundle-
   * specific / free variant (e.g. the FREE Left Breast) that the shop-wide
   * catalogue would otherwise re-price to the paid product. Absent `_bundle_raw`
   * it stays [] (prior behaviour: cart-submit falls back to the shop catalogue).
   * See _blobPositionsFromBrawList for the per-slot method logic.
   *
   * @param {Array<object>} parserBundleChildren
   * @param {Array<object>} [categoryMeta] - per-category tags + positionHandles
   * @param {string} [bundleRawStr] - the cart line's `_bundle_raw` (variant seed)
   * @returns {Array<{categoryIndex:number, name:string, productHandles:string[], positions:Array, positionHandles:string[]}>}
   */
  function _reconstructBundleCategoriesForContext(parserBundleChildren, categoryMeta, bundleRawStr) {
    var out = [];
    // Per-category SAVED position variants from `_bundle_raw` (keyed by
    // categoryIndex) → Blob A seed. null when absent/unparseable → positions [].
    var brawByCat = _brawPositionVariantsByCatIndex(bundleRawStr);
    for (var i = 0; i < parserBundleChildren.length; i++) {
      var c = parserBundleChildren[i] || {};
      var meta = _matchCategoryMeta(categoryMeta, c.products);
      var brawList = (brawByCat && brawByCat[c.categoryIndex]) || null;
      out.push({
        categoryIndex: c.categoryIndex,
        name: c.categoryName || '',
        productHandles: (c.products || []).slice(),
        // Blob A seed: the SAVED per-position pricing variant per method, so a
        // cart-edit re-save keeps a free/bundle-specific variant instead of
        // re-deriving the paid shop variant. [] (prior behaviour) without
        // `_bundle_raw`. cart-submit emits lines from the DRAFT's positions, not
        // from this array — a stale/removed position here is simply never queried.
        // Then filled from the bundle's own per-position pricing (cart-item.liquid
        // meta.positions, same source as the PDP Blob A) so a method change or a
        // newly added position/category keeps the bundle price (e.g. the FREE Left
        // Breast in print) instead of the shop's paid product.
        positions: _mergeBundlePricingPositions(_blobPositionsFromBrawList(brawList), brawList, meta && meta.positions),
        // DISPLAY-ONLY position scope: the handles this garment category actually
        // offers (bundle metaobject type_N_positions). The picker narrows its list
        // to these so a position the garment can't take is hidden, matching the PDP.
        // cart-submit never reads positionHandles → zero save-path impact. [] when
        // unmatched → picker falls open to the full (union-scoped) list.
        positionHandles: (meta && meta.positionHandles) || []
      });
    }
    return out;
  }

  function _deriveSizesFromMains(mainItems) {
    var out = [];
    for (var i = 0; i < mainItems.length; i++) {
      var m = mainItems[i];
      if (!m) continue;
      var vid = parseInt(m.variant_id || m.id, 10);
      var qty = parseInt(m.quantity, 10);
      if (!vid || vid <= 0) continue;
      if (!isFinite(qty) || qty <= 0) continue;
      out.push({
        variantId: vid,
        sizeLabel: '',
        quantity: qty,
        sku: (m.properties && m.properties._ProductSku) || ''
      });
    }
    return out;
  }

  function _extractColourOption(item) {
    if (!item) return '';
    var opts = item.options_with_values || item.optionsWithValues || [];
    for (var i = 0; i < opts.length; i++) {
      var o = opts[i];
      if (!o) continue;
      var name = String(o.name || '').toLowerCase();
      if (name === 'colour' || name === 'color') {
        return String(o.value || '');
      }
    }
    return '';
  }

  function _extractSizeOption(item) {
    if (!item) return '';
    var opts = item.options_with_values || item.optionsWithValues || [];
    for (var i = 0; i < opts.length; i++) {
      var o = opts[i];
      if (!o) continue;
      var name = String(o.name || '').toLowerCase();
      if (name === 'size') {
        return String(o.value || '');
      }
    }
    return '';
  }

  // ---------------------------------------------------------------------------
  // diff_summary helpers (Doc 9 §7.2)
  // ---------------------------------------------------------------------------

  /**
   * Compute the diff between original cart-line state and the new draft.
   * Re-runs the parser on the snapshot to get a comparable shape, then
   * walks positions / methods / logo_assignments.
   *
   * Safe: any failure returns the all-false default rather than blocking
   * the success telemetry fire. The diff is informational; absence
   * shouldn't break the save.
   *
   * @param {{items: Array, mainItems: Array, childItems: Array}} snapshot
   * @param {Object} draft - state.draft (new)
   * @returns {{positions_changed, logo_changed, methods_changed, methods_changed_detail}}
   */
  function _safeComputeDiffSummary(snapshot, draft) {
    var def = {
      positions_changed: false,
      logo_changed: false,
      methods_changed: false,
      methods_changed_detail: null
    };
    try {
      if (!JRS.ConfigurationParser || typeof JRS.ConfigurationParser.parse !== 'function') return def;
      var origDraft = JRS.ConfigurationParser.parse({
        lineItems: snapshot.items,
        flow: 'multistep'
      });
      if (!origDraft) return def;
      return _computeDiffSummary(origDraft, draft || {});
    } catch (_e) {
      return def;
    }
  }

  function _computeDiffSummary(orig, next) {
    var diff = {
      positions_changed: false,
      logo_changed: false,
      methods_changed: false,
      methods_changed_detail: null
    };

    var origPositions = (orig.positions || []).slice().sort();
    var newPositions = (next.positions || []).slice().sort();
    diff.positions_changed = JSON.stringify(origPositions) !== JSON.stringify(newPositions);

    var bothPositions = [];
    for (var i = 0; i < origPositions.length; i++) {
      if (newPositions.indexOf(origPositions[i]) !== -1) bothPositions.push(origPositions[i]);
    }

    var methodChanges = [];
    for (var j = 0; j < bothPositions.length; j++) {
      var pos = bothPositions[j];
      var origMethod = (orig.application_methods || {})[pos];
      var newMethod = (next.application_methods || {})[pos];
      if (origMethod && newMethod && origMethod !== newMethod) {
        methodChanges.push(pos + ': ' + origMethod + ' → ' + newMethod);
      }
    }
    diff.methods_changed = methodChanges.length > 0;
    diff.methods_changed_detail = methodChanges.length > 0 ? methodChanges.join('; ') : null;

    for (var k = 0; k < bothPositions.length; k++) {
      var p = bothPositions[k];
      var origAssign = (orig.logo_assignments || {})[p] || {};
      var newAssign = (next.logo_assignments || {})[p] || {};
      if (origAssign.logoId !== newAssign.logoId) {
        diff.logo_changed = true;
        break;
      }
    }
    if (diff.positions_changed) diff.logo_changed = true;

    return diff;
  }

  // ---------------------------------------------------------------------------
  // Telemetry firing
  // ---------------------------------------------------------------------------

  function _fireAnalytics(eventName, payload) {
    if (!JRS.CustomizerPopup || typeof JRS.CustomizerPopup.fireAnalytics !== 'function') return;
    try {
      JRS.CustomizerPopup.fireAnalytics(eventName, payload || {});
    } catch (err) {
      if (window.console && typeof console.warn === 'function') {
        console.warn('jrs-v2-cart-edit-flow: analytics fire failed', eventName, err);
      }
    }
  }

  function _fireLineEdited(eventPayload) {
    _fireAnalytics('pdp_customizer_line_edited', eventPayload);
  }

  function _fireCartEditFailed(failure, state, startedAt, editSessionId) {
    if (!failure) return;
    var reason = failure._reason || 'unknown';
    var critical = !!failure._critical;

    // Map reason → phase so the dashboard can faceted-count cleanly.
    var phase = 'unknown';
    if (reason === 'build_failed' || reason === 'snapshot_failed' || reason === 'missing_dependency' ||
        reason === 'missing_edit_context' || reason === 'not_in_edit_mode') {
      phase = 'preflight';
    } else if (reason === 'remove_failed' || reason === 'remove_after_add_failed' ||
               reason === 'remove_challenge_deferred') {
      phase = 'remove';
    } else if (reason === 'add_failed_rollback_succeeded' || reason === 'add_failed_rollback_failed' ||
               reason === 'add_failed_no_mutation' || reason === 'partial_add_reverted' ||
               reason === 'partial_add_revert_failed' || reason === 'add_challenge_deferred') {
      phase = 'add';
    } else if (reason === 'verify_failed') {
      phase = 'verify';
    }

    var msInSession = startedAt ? (Date.now() - (state && state.openedAt ? state.openedAt : startedAt)) : 0;

    _fireAnalytics('pdp_customizer_cart_edit_failed', {
      edit_session_id: editSessionId || (state && state.editSessionId) || null,
      reason: reason,
      phase: phase,
      error_message: failure.message || '',
      critical: critical,
      ms_in_edit_session: msInSession
    });

    // Secondary event only where a restore/revert itself failed. Other critical
    // reasons (remove_after_add_failed, verify_failed) involve no rollback.
    if (reason === 'add_failed_rollback_failed' || reason === 'partial_add_revert_failed') {
      _fireAnalytics('pdp_customizer_cart_edit_rollback_failed', {
        edit_session_id: editSessionId || (state && state.editSessionId) || null,
        error_message: failure.message || '',
        ms_in_edit_session: msInSession
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  function _verifyDependencies() {
    if (!JRS.CartAPI || typeof JRS.CartAPI.getCart !== 'function' ||
        typeof JRS.CartAPI.removeLinesSequential !== 'function' ||
        typeof JRS.CartAPI.addItems !== 'function' ||
        typeof JRS.CartAPI.readCartAuthoritative !== 'function') {
      return { ok: false, error: 'JRS.CartAPI surface missing or partial' };
    }
    if (!JRS.CartSubmit ||
        typeof JRS.CartSubmit.buildProductConfiguration !== 'function' ||
        typeof JRS.CartSubmit.submit !== 'function' ||
        typeof JRS.CartSubmit.verifyAfterSubmit !== 'function') {
      return { ok: false, error: 'JRS.CartSubmit surface missing or partial' };
    }
    if (!JRS.CartPayloadBuilder || typeof JRS.CartPayloadBuilder.build !== 'function') {
      return { ok: false, error: 'JRS.CartPayloadBuilder unavailable' };
    }
    return { ok: true };
  }

  function _failure(reason, message, opts) {
    var err = new Error(message);
    err._cartEditFailure = true;
    err._reason = reason;
    err._critical = !!(opts && opts.critical);
    err._rollbackOk = !!(opts && opts.rollbackOk);
    err._reloadRequired = !!(opts && opts.reloadRequired);
    return err;
  }

  /** Guard failure: emits pdp_customizer_cart_edit_failed, then returns the result. */
  function _guardFailure(reason, message, state, startedAt) {
    _fireCartEditFailed(_failure(reason, message), state, startedAt, state && state.editSessionId);
    return _failureResult(reason, message);
  }

  function _failureResult(reason, message, opts) {
    return {
      ok: false,
      reason: reason || 'unknown',
      error: message || '',
      critical: !!(opts && opts.critical),
      rollbackOk: !!(opts && opts.rollbackOk),
      reloadRequired: !!(opts && opts.reloadRequired)
    };
  }

  function _deepClone(input) {
    try {
      return JSON.parse(JSON.stringify(input));
    } catch (_e) {
      return null;
    }
  }

  function _sumQuantities(items) {
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      var n = parseInt(items[i] && items[i].quantity, 10);
      if (isFinite(n) && n > 0) total += n;
    }
    return total;
  }

  function _firstParentProperties(addedItems) {
    for (var i = 0; i < addedItems.length; i++) {
      var item = addedItems[i];
      if (!item) continue;
      var props = item.properties || {};
      if (props._ParentVariant === undefined) return props;
    }
    return (addedItems[0] && addedItems[0].properties) || {};
  }

  /**
   * Strip non-structured fields out of a properties object before
   * sending to telemetry per Doc 3 §4.14 PII note: original_properties_
   * snapshot must NOT contain `Logo Text` lines, `Note` content, or
   * filename strings. We strip those keys to be safe.
   *
   * Allowed keys: anything not in the PII denylist.
   *
   * @param {Object|null} props
   * @returns {Object}
   */
  function _structuredProperties(props) {
    if (!props || typeof props !== 'object') return {};
    var denyPrefixes = ['Logo Text ', 'Logo Filename ', 'Note', 'note', 'Custom Note',
      // B4 — hidden-renamed forms carry the same PII
      '_Logo Text', '_Logo Filename', '_Note'];
    var denyExact = ['_FileURL'];
    var out = {};
    var keys = Object.keys(props);
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      if (denyExact.indexOf(k) !== -1) continue;
      var denied = false;
      for (var p = 0; p < denyPrefixes.length; p++) {
        if (k.indexOf(denyPrefixes[p]) === 0) { denied = true; break; }
      }
      if (denied) continue;
      out[k] = props[k];
    }
    return out;
  }

  // ---------------------------------------------------------------------------
  // Public surface + auto-init
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Per-product cart-edit conversion shim (Phase per_product S4, spec D10)
  // ---------------------------------------------------------------------------
  //
  // The parser (_parseBundle) emits a FLAT per_product draft: positions[],
  // application_methods{}, logo_assignments{} + a position_categories map
  // (positionId → real categoryIndex) + _bundle_children. The per_product flow
  // and review (S1-S3) read draft.category_configs, NOT the flat draft. This
  // pure shim converts the parser's flat shape into the category_configs
  // accumulator so a cart-edit reopen lands on the per-product review with each
  // category's config intact, and the edit-from-review round-trip + save reuse
  // the S3 paths unchanged.
  //
  // Pure: takes the parsed draft, returns a NEW converted draft. No globals,
  // no dispatch — the shell (openFromCartLine) dispatches the result via
  // openInEditMode. bundle_wide cart-edit never calls this (no-ops defensively
  // on any other branding_strategy).
  //
  // @param {Object} parsedDraft - JRS.ConfigurationParser._parseBundle output
  // @returns {Object} converted draft (category_configs populated, flat cleared)
  // --- _bundle_raw position reconciliation (cart-edit) -----------------------
  // The parser rebuilds per-category positions from the flat numbered `Position N`
  // props, which record a UNIFORM position (identical logo + method across every
  // garment) only ONCE — so on reconstruction it lands in a single category. But
  // `_bundle_raw` (the authoritative structure the cart summary AND backend
  // fulfilment read) carries the full per-category position list. Left unfixed,
  // the cart-edit review under-shows those positions AND a Save would drop them.
  //
  // This reconciliation is ADDITIVE-ONLY: it adds back any position a category is
  // missing per `_bundle_raw`, copying the (uniform) logo assignment from whichever
  // category kept it. It NEVER removes a position, so a true per_product config
  // (whose per-category positions already agree with `_bundle_raw`) is untouched.
  // Guarded: a category is only reconciled when it shares ≥1 position with its
  // `_bundle_raw` counterpart — so a slot/index misalignment can never corrupt
  // (worst case: no change, i.e. the prior behaviour). No-op without a parseable
  // `_bundle_raw`, so the numbered-props reconstruction remains the fallback.
  function _brawIsArray(x) { return Object.prototype.toString.call(x) === '[object Array]'; }

  // True for a form-encoded "array" as /cart.js returns it: a plain object whose
  // keys are all non-negative integers ("0","1",…).
  function _brawIsIndexMap(x) {
    if (!x || typeof x !== 'object' || _brawIsArray(x)) return false;
    var keys = Object.keys(x);
    if (!keys.length) return false;
    for (var i = 0; i < keys.length; i++) { if (!/^\d+$/.test(keys[i])) return false; }
    return true;
  }

  // {"0": a, "1": b, …} → [a, b, …] in numeric key order.
  function _brawIndexMapToArray(x) {
    var keys = Object.keys(x).sort(function (a, b) { return Number(a) - Number(b); });
    var out = [];
    for (var i = 0; i < keys.length; i++) out.push(x[keys[i]]);
    return out;
  }

  function _brawHandleize(name) {
    return String(name == null ? '' : name).toLowerCase().replace(/[\s\-]+/g, '_').replace(/[^a-z0-9_]/g, '');
  }

  function _brawNormMethod(m) {
    var s = String(m == null ? '' : m).toLowerCase();
    if (s.indexOf('print') !== -1) return 'print';
    if (s.indexOf('embroid') !== -1) return 'embroidery';
    return s || null;
  }

  // Parse `_bundle_raw` → { catIndex (0-based, slot order) : [{id, title, method}] }.
  // Tolerant of double-encoded JSON; handles V2 (schema_version 2, categories[] with
  // position_title) and V1 (array of categories with positions[].name). null when
  // absent/unparseable → caller falls back to the numbered-props reconstruction.
  function _brawPositionsByIndex(rawStr) {
    if (!rawStr) return null;
    var parsed;
    try {
      parsed = JSON.parse(rawStr);
      if (typeof parsed === 'string') { parsed = JSON.parse(parsed); }
    } catch (e) { return null; }
    var isV2 = !_brawIsArray(parsed) && !!parsed &&
      (parsed.schema_version === 2 || parsed.schema_version === '2');
    var cats;
    if (isV2) {
      cats = _brawIsArray(parsed.categories) ? parsed.categories.slice() : [];
      cats.sort(function (a, b) {
        var sa = (a && typeof a.slot === 'number') ? a.slot : 0;
        var sb = (b && typeof b.slot === 'number') ? b.slot : 0;
        return sa - sb;
      });
    } else if (_brawIsArray(parsed)) {
      cats = parsed;
    } else {
      return null;
    }
    var byIndex = {};
    for (var ci = 0; ci < cats.length; ci++) {
      var positions = (cats[ci] && _brawIsArray(cats[ci].positions)) ? cats[ci].positions : [];
      var list = [];
      for (var p = 0; p < positions.length; p++) {
        var pos = positions[p] || {};
        var title = isV2 ? pos.position_title : (pos.name || pos.position);
        if (title == null || String(title) === '') continue;
        var method = isV2 ? pos.method : (pos.applicationMethod || pos['Application Method']);
        list.push({ id: _brawHandleize(title), title: String(title), method: _brawNormMethod(method) });
      }
      byIndex[ci] = list;
    }
    return byIndex;
  }

  // Catalogue id from a position display name — MUST match the position
  // catalogue's _normaliseName (and cart-submit's _handleizePosition) so the
  // shop-catalogue lookup in _blobPositionsFromBrawList hits the same entry
  // cart-submit will resolve for that position.
  function _brawCatalogueId(name) {
    return String(name == null ? '' : name).toLowerCase()
      .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  // Parse `_bundle_raw` → { categoryIndex : [{ title, method, variantId }] },
  // keyed by each category's OWN categoryIndex (V1) or slot order (V2), KEEPING
  // the per-position logo variant id. Unlike _brawPositionsByIndex (drops the
  // variant, keys by array position) this feeds the Blob A seed below so cart-
  // edit can preserve a bundle-specific pricing variant. null when unparseable.
  function _brawPositionVariantsByCatIndex(rawStr) {
    if (!rawStr) return null;
    var parsed;
    // #78864 (2026-10-02): the production parent is submitted form-encoded, so
    // /cart.js returns `_bundle_raw` / `_bundle_meta` as a nested OBJECT keyed
    // "0","1",… (positions keyed the same way), not a JSON string. JSON.parse on
    // that threw → null → no seed → cart-edit re-priced the free Left Breast to
    // the paid shop variant. Accept the object as-is (same as the parser's
    // _attachCategoryProductMeta) and turn "0","1",… maps into arrays.
    if (typeof rawStr === 'object') {
      parsed = rawStr;
    } else {
      try {
        parsed = JSON.parse(rawStr);
        if (typeof parsed === 'string') { parsed = JSON.parse(parsed); }
      } catch (e) { return null; }
    }
    var isV2 = !_brawIsArray(parsed) && !!parsed &&
      (parsed.schema_version === 2 || parsed.schema_version === '2');
    var cats;
    if (isV2) {
      cats = _brawIsArray(parsed.categories) ? parsed.categories.slice() : [];
      cats.sort(function (a, b) {
        var sa = (a && typeof a.slot === 'number') ? a.slot : 0;
        var sb = (b && typeof b.slot === 'number') ? b.slot : 0;
        return sa - sb;
      });
    } else if (_brawIsArray(parsed)) {
      cats = parsed;
    } else if (_brawIsIndexMap(parsed)) {
      cats = _brawIndexMapToArray(parsed);
    } else {
      return null;
    }
    var byCat = {};
    for (var ci = 0; ci < cats.length; ci++) {
      var cat = cats[ci] || {};
      // Form-encoded scalars arrive as strings ("0"), so parse rather than typeof-check.
      var catIdx = parseInt(cat.categoryIndex, 10);
      var key = (!isNaN(catIdx) && catIdx >= 0) ? catIdx : ci;
      var positions = _brawIsArray(cat.positions) ? cat.positions
        : (_brawIsIndexMap(cat.positions) ? _brawIndexMapToArray(cat.positions) : []);
      var list = [];
      for (var p = 0; p < positions.length; p++) {
        var pos = positions[p] || {};
        var title = isV2 ? pos.position_title : (pos.name || pos.position);
        if (title == null || String(title) === '') continue;
        var method = isV2 ? pos.method : (pos.applicationMethod || pos['Application Method']);
        // Since the 2026-08-04 ERP-parity change, `logoVariantId` is the LOGO
        // product variant ("Uploaded Logo", logo-options) and the POSITION
        // pricing variant is `positionVariantId` (+ the method-specific
        // embroideryVariantId / printVariantId). Prefer those; legacy carts,
        // where logoVariantId WAS the position variant, fall back unchanged —
        // same rule as the parser's _positionVariantFromCustomizationData.
        var methodVid = (_brawNormMethod(method) === 'print') ? pos.printVariantId : pos.embroideryVariantId;
        var vid = parseInt(pos.positionVariantId || methodVid ||
          pos.logoVariantId || pos.logo_variant_id || pos.variantId || pos.logoVariant, 10) || 0;
        list.push({ title: String(title), method: _brawNormMethod(method), variantId: vid });
      }
      byCat[key] = list;
    }
    return byCat;
  }

  // Build cart-submit "Blob A" entries ({ name, embroideryVariantId,
  // printVariantId }) for ONE category from its `_bundle_raw` positions. The
  // SAVED pricing variant goes in the slot matching the position's ORIGINAL
  // method; the OTHER method's slot is filled from the shop-wide catalogue so
  // CHANGING a position's method on cart-edit still re-derives the correct paid
  // variant (cart-submit._resolvePerProductVariantId prefers the chosen-method
  // slot, then falls back emb→prn — without the other slot it would wrongly
  // reuse the saved variant on the new method). Positions with no saved variant
  // are skipped → shop-catalogue fallback (prior behaviour).
  function _blobPositionsFromBrawList(brawList) {
    var out = [];
    if (!_brawIsArray(brawList)) return out;
    var cat = (JRS && JRS.PositionCatalogue) || null;
    for (var i = 0; i < brawList.length; i++) {
      var bp = brawList[i] || {};
      var vid = parseInt(bp.variantId, 10) || 0;
      if (vid <= 0) continue;
      var shop = (cat && typeof cat.getVariantIdsFor === 'function')
        ? cat.getVariantIdsFor(_brawCatalogueId(bp.title)) : null;
      var emb = (shop && shop.embroidery) || 0;
      var prn = (shop && shop.print) || 0;
      if (bp.method === 'print') { prn = vid; } else { emb = vid; }
      out.push({ name: String(bp.title), embroideryVariantId: emb, printVariantId: prn });
    }
    return out;
  }

  // Merge the bundle's own per-position pricing variants (cart-item.liquid
  // data-jrs-bundle-category-meta → positions[], built like bundle-new.liquid Blob A)
  // into the `_bundle_raw` seed. The SAVED variant keeps its own method slot (faithful
  // re-save); the OTHER method slot takes the bundle's variant over the shop-catalogue
  // one, so switching a free Left Breast from embroidery to print stays free (the
  // bundle's "Free Left Breast - Print"). Bundle positions with no saved entry are
  // appended, so a position or category added during the edit is priced as on the
  // PDP. A 0 bundle id never overwrites a known one. No meta → seed unchanged.
  function _mergeBundlePricingPositions(seeded, brawList, metaPositions) {
    var out = (seeded || []).slice();
    if (!_brawIsArray(metaPositions) || !metaPositions.length) return out;
    var byId = {};
    for (var i = 0; i < out.length; i++) byId[_brawCatalogueId(out[i].name)] = i;
    // Saved method per position id, from the same `_bundle_raw` list the seed used.
    var savedMethod = {};
    if (_brawIsArray(brawList)) {
      for (var b = 0; b < brawList.length; b++) {
        var bp = brawList[b] || {};
        if ((parseInt(bp.variantId, 10) || 0) > 0) savedMethod[_brawCatalogueId(bp.title)] = (bp.method === 'print') ? 'print' : 'embroidery';
      }
    }
    for (var m = 0; m < metaPositions.length; m++) {
      var mp = metaPositions[m] || {};
      if (!mp.name) continue;
      var memb = parseInt(mp.embroideryVariantId, 10) || 0;
      var mprn = parseInt(mp.printVariantId, 10) || 0;
      if (memb <= 0 && mprn <= 0) continue;
      var membP = (typeof mp.embroideryPrice === 'number') ? mp.embroideryPrice : null;
      var mprnP = (typeof mp.printPrice === 'number') ? mp.printPrice : null;
      var idx = byId[_brawCatalogueId(mp.name)];
      if (idx == null) {
        out.push({ name: String(mp.name), embroideryVariantId: memb, printVariantId: mprn,
                   embroideryPrice: membP, printPrice: mprnP });
        byId[_brawCatalogueId(mp.name)] = out.length - 1;
        continue;
      }
      var cur = out[idx];
      var merged = { name: cur.name, embroideryVariantId: cur.embroideryVariantId, printVariantId: cur.printVariantId,
                     embroideryPrice: membP, printPrice: mprnP };
      // Only the slot NOT holding the saved variant takes the bundle's id.
      var sm = savedMethod[_brawCatalogueId(mp.name)];
      if (sm === 'print') { if (memb > 0) merged.embroideryVariantId = memb; }
      else if (sm === 'embroidery') { if (mprn > 0) merged.printVariantId = mprn; }
      out[idx] = merged;
    }
    return out;
  }

  function _reconcileBundleRawPositions(configs, rawStr) {
    if (!configs) return configs;
    var byIndex = _brawPositionsByIndex(rawStr);
    if (!byIndex) return configs; // unparseable → keep the numbered-props reconstruction

    // Template: positionId → {method, assignment} from any category that HAS it
    // (a uniform/deduped position survives in ≥1 category with its real logo).
    var tmpl = {};
    var ck;
    for (ck in configs) {
      if (!Object.prototype.hasOwnProperty.call(configs, ck)) continue;
      var cfgT = configs[ck] || {};
      var posT = cfgT.positions || [];
      for (var t = 0; t < posT.length; t++) {
        var pidT = posT[t];
        if (!tmpl[pidT]) {
          tmpl[pidT] = {
            method: (cfgT.application_methods || {})[pidT],
            assignment: (cfgT.logo_assignments || {})[pidT]
          };
        }
      }
    }

    for (ck in configs) {
      if (!Object.prototype.hasOwnProperty.call(configs, ck)) continue;
      var raw = byIndex[ck]; // string key coerces to the numeric slot index
      if (!raw || !raw.length) continue;
      var cfg = configs[ck] || {};
      if (!_brawIsArray(cfg.positions)) cfg.positions = [];
      if (!cfg.application_methods) cfg.application_methods = {};
      if (!cfg.logo_assignments) cfg.logo_assignments = {};

      var existing = {};
      for (var e = 0; e < cfg.positions.length; e++) existing[cfg.positions[e]] = true;

      // Guard: only reconcile a category that shares ≥1 position with its
      // `_bundle_raw` counterpart (proves alignment). A mismatch skips → no corruption.
      var shares = false;
      for (var s = 0; s < raw.length; s++) { if (existing[raw[s].id]) { shares = true; break; } }
      if (!shares) continue;

      for (var r = 0; r < raw.length; r++) {
        var rid = raw[r].id;
        if (!rid || existing[rid]) continue;        // already present
        var tm = tmpl[rid];
        if (!tm || tm.assignment == null) continue;  // no known logo → don't fabricate
        cfg.positions.push(rid);
        cfg.application_methods[rid] = raw[r].method || tm.method || null;
        cfg.logo_assignments[rid] = tm.assignment;   // same uniform logo
        existing[rid] = true;
      }
    }
    return configs;
  }

  // @param {Object} parsedDraft - JRS.ConfigurationParser._parseBundle output
  // @param {string} [bundleRawStr] - the cart line's `_bundle_raw` value; when
  //   present, per-category positions are reconciled against it (see above).
  function convertToPerProduct(parsedDraft, bundleRawStr) {
    var draft = parsedDraft || {};
    if (draft.branding_strategy !== 'per_product') return draft; // defensive no-op

    // Idempotent: the parser now emits category_configs directly (repeating-
    // position safe, keyed per category via `_logoproduct N`). When it's already
    // populated, pass it through — but still reconcile against `_bundle_raw` so a
    // uniform position recorded once (→ one category) is restored to all the
    // categories `_bundle_raw` places it in. Legacy flat drafts (no
    // category_configs) still take the build path below (also reconciled).
    if (draft.category_configs &&
        Object.keys(draft.category_configs).length > 0) {
      _reconcileBundleRawPositions(draft.category_configs, bundleRawStr);
      return draft;
    }

    var positionCategories = draft.position_categories || {};
    var flatPositions = draft.positions || [];
    var flatMethods = draft.application_methods || {};
    var flatAssignments = draft.logo_assignments || {};
    var flatText = draft.text_logo || null;

    // Group flat positions by their real categoryIndex (from position_categories).
    var configs = {};
    for (var p = 0; p < flatPositions.length; p++) {
      var pid = flatPositions[p];
      var catIdx = positionCategories[pid];
      if (typeof catIdx !== 'number' || catIdx < 0) continue; // unmapped — skip defensively
      if (!configs[catIdx]) {
        configs[catIdx] = {
          positions: [],
          application_methods: {},
          logo_assignments: {},
          logo_strategy: null,
          text_logo: flatText,
          completed: true
        };
      }
      configs[catIdx].positions.push(pid);
      if (flatMethods[pid] != null) configs[catIdx].application_methods[pid] = flatMethods[pid];
      if (flatAssignments[pid] != null) configs[catIdx].logo_assignments[pid] = flatAssignments[pid];
    }

    // Restore any uniform position the numbered props recorded once (additive,
    // guarded, no-op without a parseable _bundle_raw) BEFORE deriving strategy.
    _reconcileBundleRawPositions(configs, bundleRawStr);

    // Derive logo_strategy per category from its assignment pattern.
    var catCount = 0;
    for (var k in configs) {
      if (!Object.prototype.hasOwnProperty.call(configs, k)) continue;
      configs[k].logo_strategy = _deriveCategoryLogoStrategy(configs[k].positions, configs[k].logo_assignments);
      catCount++;
    }

    // Clone the parsed draft, overlay the per_product accumulator, clear the
    // flat scratch. _bundle_children / position_categories / _bundle_meta / flow
    // are preserved (the review's category-name lookup + the save's synthetic
    // context read _bundle_children; position_categories stays as a parser
    // artifact but is no longer read downstream — category_configs is canonical).
    var out = {};
    for (var f in draft) {
      if (Object.prototype.hasOwnProperty.call(draft, f)) out[f] = draft[f];
    }
    out.category_configs = configs;
    out._current_category_index = catCount; // == category count → flow won't loop; lands on review
    out._edit_category_index = null;
    out.positions = [];
    out.application_methods = {};
    out.logo_assignments = {};
    out.logo_strategy = null;
    out.text_logo = null;
    return out;
  }

  // same_logo when every position in the category shares one logoId (or <2
  // positions); different_logos otherwise. Scoped to one category's assignments.
  function _deriveCategoryLogoStrategy(positions, assignments) {
    if (!positions || positions.length < 2) return 'same_logo';
    var firstId = null;
    for (var i = 0; i < positions.length; i++) {
      var a = assignments[positions[i]];
      var id = (a && a.logoId) || null;
      if (i === 0) { firstId = id; continue; }
      if (id !== firstId) return 'different_logos';
    }
    return 'same_logo';
  }

  JRS.CartEditFlow = {
    init: init,
    destroy: destroy,
    saveChanges: saveChanges,
    cancel: cancel,
    // Phase per_product S4 — cart-edit conversion shim (parser → category_configs).
    convertToPerProduct: convertToPerProduct,
    // Internal seams for tests / diagnostics:
    _reconcileBundleRawPositions: _reconcileBundleRawPositions,
    _brawPositionsByIndex: _brawPositionsByIndex,
    _brawPositionVariantsByCatIndex: _brawPositionVariantsByCatIndex,
    _blobPositionsFromBrawList: _blobPositionsFromBrawList,
    _mergeBundlePricingPositions: _mergeBundlePricingPositions,
    _computeDiffSummary: _computeDiffSummary,
    _snapshotRelatedItems: _snapshotRelatedItems,
    _resumePendingEdit: _resumePendingEdit,
    _missingByIdentity: _missingByIdentity,
    PENDING_EDIT_KEY: PENDING_EDIT_KEY,
    _buildSyntheticContext: _buildSyntheticContext,
    _hydratePdpContextForCartEdit: _hydratePdpContextForCartEdit,
    _readProductTagsFromDom: _readProductTagsFromDom,
    _resolveProductTagsForCartLine: _resolveProductTagsForCartLine,
    _structuredProperties: _structuredProperties,
    _deriveCategoryLogoStrategy: _deriveCategoryLogoStrategy,
    _reconstructBundleCategoriesForContext: _reconstructBundleCategoriesForContext,
    // Per-category branding meta (child tags + position handles) for per_product
    // bundle cart-edit — re-emitted on the cart row from the bundle metaobject.
    _translateBundleChildrenForContext: _translateBundleChildrenForContext,
    _matchCategoryMeta: _matchCategoryMeta,
    _readBundleCategoryMetaFromDom: _readBundleCategoryMetaFromDom
  };

  // Auto-init at IIFE eval. The script is deferred + Tier 3 cart-only,
  // so by the time it runs the DOM is fully parsed; EventBus is in Tier 1
  // and has loaded before this. No DOMContentLoaded wait needed.
  init();
})(window.JRS);
